import React, { useEffect, useMemo, useRef, useState } from "react";

import { Spinner } from "./shared";
import { FoodLabelLink, LabelMatch, LabelSearchResult } from "./types";
import type { CatalogApi } from "./useCatalogApi";

/**
 * "Linked labels" control shown inside FoodDetailPanel.
 *
 * A catalog food only becomes a first-class search result once it is linked to
 * one or more canonical labels. This widget makes the link state prominent (it
 * is easy to miss otherwise) and supports MULTIPLE labels per food.
 *
 * Two modes:
 *  - Live (foodId provided): loads the food's current link(s) and links a chosen
 *    label immediately via the API (add-only; there is no unlink endpoint).
 *  - Controlled (no foodId): the parent owns the chosen list via `value`/
 *    `onChange`; the labels are written after the food is created/updated.
 *  - Staged (foodId + stageForReview): shows the food's current links, and new
 *    picks go to `value`/`onChange` so they travel with an edit that needs
 *    Passio review (linked on approval).
 *
 * `suggestions` are candidate leaf-label OPTIONS (for ingest items, the wide-net
 * labels the ingest retrieved; for existing foods, name/brand lookups) offered as
 * one-tap "add" chips — no match percentages are shown. `candidateLabels` are raw
 * proposed phrases, shown when nothing is linked so the reviewer has a hint.
 */
export interface LabelLinkProps {
  api: Pick<CatalogApi, "searchLabels" | "getFoodLabels" | "linkFoodLabel">;
  /** When set, the food already exists and links are applied live. */
  foodId?: string;
  source?: string;
  /** Auto-matched labels to offer as one-tap "add" chips. */
  suggestions?: LabelMatch[];
  /** Category phrases proposed by ingest (shown when nothing is linked). */
  candidateLabels?: string[];
  /** Controlled chosen labels (pre-ingest). */
  value?: FoodLabelLink[];
  onChange?: (labels: FoodLabelLink[]) => void;
  disabled?: boolean;
  /** With foodId: stage new labels in `value` instead of linking them live. */
  stageForReview?: boolean;
}

const dedupeByLabel = (labels: FoodLabelLink[]): FoodLabelLink[] => {
  const seen = new Set<string>();
  const out: FoodLabelLink[] = [];
  for (const l of labels) {
    if (!l?.labelId || seen.has(l.labelId)) continue;
    seen.add(l.labelId);
    out.push(l);
  }
  return out;
};

export const LabelLink: React.FC<LabelLinkProps> = ({
  api,
  foodId,
  source = "passio",
  suggestions,
  candidateLabels,
  value,
  onChange,
  disabled,
  stageForReview,
}) => {
  const staged = !!foodId && !!stageForReview;
  const isLive = !!foodId && !staged;

  const [loadedLinks, setLoadedLinks] = useState<FoodLabelLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [picking, setPicking] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<LabelSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const searchSeq = useRef(0);

  // Load current link(s) for an existing food.
  const reload = useMemo(
    () => async () => {
      if (!foodId) return;
      setLoading(true);
      try {
        const links = await api.getFoodLabels(foodId, source);
        setLoadedLinks(links);
      } catch {
        // non-fatal; leave as "no label"
      } finally {
        setLoading(false);
      }
    },
    [api, foodId, source]
  );

  useEffect(() => {
    void reload();
  }, [reload]);

  // Debounced label search.
  useEffect(() => {
    if (!picking) return;
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    const seq = ++searchSeq.current;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.searchLabels(q);
        if (seq === searchSeq.current) setResults(r);
      } catch {
        if (seq === searchSeq.current) setResults([]);
      } finally {
        if (seq === searchSeq.current) setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [api, query, picking]);

  // The labels currently attached (live: from the server; controlled: the
  // parent-owned chosen list; staged: both).
  const selected: FoodLabelLink[] = useMemo(() => {
    if (staged) return dedupeByLabel([...loadedLinks, ...(value || [])]);
    return isLive ? loadedLinks : value || [];
  }, [staged, isLive, loadedLinks, value]);
  const selectedIds = useMemo(
    () => new Set(selected.map((l) => l.labelId)),
    [selected]
  );
  const linkedIds = useMemo(
    () => new Set(loadedLinks.map((l) => l.labelId)),
    [loadedLinks]
  );
  const isPendingLabel = (labelId: string): boolean =>
    staged && !linkedIds.has(labelId);
  const canRemove = (labelId: string): boolean =>
    !disabled && (staged ? !linkedIds.has(labelId) : !isLive);

  // Add a label (live -> link immediately; controlled -> append to value).
  const addLabel = async (link: FoodLabelLink) => {
    setError(null);
    if (selectedIds.has(link.labelId)) return;
    if (isLive && foodId) {
      setLinking(true);
      try {
        await api.linkFoodLabel({ labelId: link.labelId, foodId, source });
        await reload();
      } catch (e) {
        setError(
          e instanceof Error ? e.message : "Failed to link label (try another)."
        );
      } finally {
        setLinking(false);
      }
    } else {
      onChange?.(dedupeByLabel([...(value || []), link]));
    }
  };

  const removeLabel = (labelId: string) => {
    // Live links have no unlink endpoint; only labels not yet linked can be
    // removed here.
    if (!canRemove(labelId)) return;
    onChange?.((value || []).filter((l) => l.labelId !== labelId));
  };

  const chooseResult = async (r: LabelSearchResult) => {
    await addLabel({
      labelId: r.labelId,
      displayName: r.displayName,
      synonym: r.synonym,
      score: r.score,
    });
    setQuery("");
    setResults([]);
    // Keep the picker open so several labels can be added in one go.
  };

  // Auto-match suggestions not already selected -> one-tap "add" chips.
  const pendingSuggestions = (suggestions || []).filter(
    (s) => !selectedIds.has(s.labelId)
  );

  const hasSelection = selected.length > 0;
  const proposed = (candidateLabels || []).filter(Boolean);

  return (
    <div
      className={`rounded-lg border-2 p-3 ${
        hasSelection
          ? "border-emerald-200 bg-emerald-50/40"
          : "border-amber-300 bg-amber-50"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-base leading-none">🏷️</span>
          <span className="text-sm font-bold text-gray-800">Search labels</span>
          <span className="text-[11px] font-normal text-gray-500">
            (links this food into search)
          </span>
        </div>
        {!disabled && !picking && (
          <button
            type="button"
            onClick={() => {
              setPicking(true);
              setQuery("");
              setResults([]);
            }}
            className="flex-shrink-0 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700"
          >
            {hasSelection ? "+ Add label" : "Link a label"}
          </button>
        )}
      </div>

      {/* Current state */}
      <div className="mt-2">
        {loading ? (
          <Spinner label="loading labels" />
        ) : hasSelection ? (
          <div className="flex flex-wrap items-center gap-1.5">
            {selected.map((l) => (
              <span
                key={l.labelId}
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${
                  isPendingLabel(l.labelId)
                    ? "bg-amber-50 text-amber-800 ring-amber-200"
                    : "bg-emerald-100 text-emerald-800 ring-emerald-200"
                }`}
              >
                {l.displayName}
                {l.isDefault && (
                  <span className="rounded bg-emerald-200 px-1 text-[10px] font-medium text-emerald-800">
                    default
                  </span>
                )}
                {isPendingLabel(l.labelId) && (
                  <span className="rounded bg-amber-100 px-1 text-[10px] font-medium text-amber-800">
                    after review
                  </span>
                )}
                {canRemove(l.labelId) && (
                  <button
                    type="button"
                    onClick={() => removeLabel(l.labelId)}
                    className="ml-0.5 text-emerald-600 hover:text-emerald-900"
                    aria-label={`Remove ${l.displayName}`}
                    title="Remove"
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
          </div>
        ) : (
          <div className="text-sm font-semibold text-amber-800">
            ⚠ Not linked to any label — this item won&apos;t surface well in
            search.
            {proposed.length > 0 && (
              <div className="mt-0.5 text-xs font-normal text-amber-700">
                Proposed categories: {proposed.join(", ")}
              </div>
            )}
          </div>
        )}
        {staged && !loading && (
          <p className="mt-1.5 text-[11px] leading-snug text-gray-500">
            Labels you add here are sent with your change and linked once Passio
            approves it.
          </p>
        )}
      </div>

      {/* Options — one-tap add (from the ingest's retrieved leaf labels) */}
      {!disabled && pendingSuggestions.length > 0 && (
        <div className="mt-2">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Options {hasSelection ? "(add more)" : "(tap to link)"}
          </div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {pendingSuggestions.map((s) => (
              <button
                key={s.labelId}
                type="button"
                disabled={linking}
                onClick={() =>
                  void addLabel({
                    labelId: s.labelId,
                    displayName: s.displayName,
                    synonym: s.synonym,
                  })
                }
                className="inline-flex items-center gap-1 rounded-full border border-indigo-300 bg-white px-2.5 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
              >
                <span className="text-indigo-500">+</span>
                {s.displayName}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Picker */}
      {picking && (
        <div className="mt-2 rounded-md border border-gray-200 bg-white p-2">
          <div className="flex items-center gap-2">
            <input
              autoFocus
              type="text"
              value={query}
              placeholder="Search labels (e.g. frozen pizza)…"
              onChange={(e) => setQuery(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-indigo-500 focus:outline-none"
            />
            <button
              type="button"
              onClick={() => {
                setPicking(false);
                setQuery("");
              }}
              className="text-xs font-medium text-gray-500 hover:text-gray-700"
            >
              Done
            </button>
          </div>
          {error && (
            <div className="mt-1 text-[11px] text-red-600">{error}</div>
          )}
          <div className="mt-2 max-h-48 overflow-y-auto">
            {searching || linking ? (
              <Spinner label={linking ? "linking" : "searching"} />
            ) : query.trim().length < 2 ? (
              <div className="px-1 py-1 text-[11px] text-gray-400">
                Type at least 2 characters.
              </div>
            ) : results.length === 0 ? (
              <div className="px-1 py-1 text-[11px] text-gray-400">
                No matching labels.
              </div>
            ) : (
              <ul className="space-y-0.5">
                {results.map((r) => {
                  const already = selectedIds.has(r.labelId);
                  return (
                    <li key={r.labelId}>
                      <button
                        type="button"
                        disabled={already}
                        onClick={() => void chooseResult(r)}
                        className={`flex w-full items-center justify-between rounded px-2 py-1 text-left text-sm ${
                          already
                            ? "cursor-default text-gray-400"
                            : "hover:bg-indigo-50"
                        }`}
                      >
                        <span className="truncate">{r.displayName}</span>
                        {already && (
                          <span className="text-[11px] text-emerald-600">
                            ✓ added
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
